'use client';

import { DecoratorNode } from 'lexical';
import * as React from 'react';
import { JSX } from 'react';

export class ImageNode extends DecoratorNode {
  __src;
  __alt;
  __width;
  __height;
  __align;

  static getType() {
    return 'image';
  }

  static clone(node) {
    return new ImageNode(
      { src: node.__src, alt: node.__alt, width: node.__width, height: node.__height, align: node.__align },
      node.__key
    );
  }

  constructor({ src, alt, width = null, height = null, align = 'center' }, key) {
    super(key);
    this.__src = src;
    this.__alt = alt;
    this.__width = width;
    this.__height = height;
    this.__align = align;
  }

  createDOM() {
    const el = document.createElement('div');
    el.className = this._wrapperClass();
    return el;
  }

  updateDOM(prevNode, dom) {
    if (prevNode.__align !== this.__align) {
      dom.className = this._wrapperClass();
    }
    return false;
  }

  _wrapperClass() {
    switch (this.__align) {
      case 'left': return 'my-4 float-left mr-4 max-w-[50%]';
      case 'right': return 'my-4 float-right ml-4 max-w-[50%]';
      case 'full': return 'my-4 w-full';
      default: return 'my-4 flex justify-center';
    }
  }

  decorate() {
    const w = this.__width ?? 800;
    const h = this.__height ?? 450;
    const alt = this.__alt ?? '';
    return (
      <div className={this._wrapperClass()}>
        {/* next/image will still lazyload client-side; you can swap for <img> */}
        <img
          src={this.__src}
          alt={alt}
          width={w}
          height={h}
          // unoptimized={!this.__src.startsWith('https://res.cloudinary.com')} // allow external URLs
          className={this.__align === 'full' ? 'w-full h-auto rounded-lg' : 'rounded-lg'}
        />
      </div>
    );
  }

  exportJSON() {
    return {
      type: 'image',
      version: 1,
      src: this.__src,
      alt: this.__alt,
      width: this.__width,
      height: this.__height,
      align: this.__align,
    };
  }

  static importJSON(serialized) {
    return new ImageNode({
      src: serialized.src,
      alt: serialized.alt,
      width: serialized.width ?? null,
      height: serialized.height ?? null,
      align: serialized.align ?? 'center',
    });
  }
}

export function $createImageNode(payload) {
  return new ImageNode(payload);
}
export function $isImageNode(node) {
  return node instanceof ImageNode;
}
